import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { authAPI, setAuthToken } from '../config/api';
import { showAppToast } from '../utils/notifications';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const REMEMBERED_LOGIN_EMAIL_KEY = 'deroyalty.rememberedLoginEmail';

function getPostLoginPath(role) {
  return role?.toLowerCase() === 'admin' ? '/admin' : '/dashboard';
}

function getRememberedLoginEmail() {
  try {
    return localStorage.getItem(REMEMBERED_LOGIN_EMAIL_KEY) || '';
  } catch (error) {
    return '';
  }
}

// Separate component so useGoogleLogin only runs when a client ID is available.
// This prevents the "Missing required parameter client_id" crash.
function GoogleSignInButton({ onLoginSuccess, navigate, setLoading, loading }) {
  const handleGoogleAuth = useGoogleLogin({
    onSuccess: async (credentialResponse) => {
      setLoading(true);
      try {
        const accessToken = credentialResponse.access_token;
        if (!accessToken) {
          showAppToast({ message: 'Google authentication failed - no access token returned.', type: 'error', duration: 3400 });
          return;
        }

        const response = await authAPI.googleSignin(accessToken);
        const { token, user } = response.data;

        if (token && user) {
          setAuthToken(token, user.role);
          onLoginSuccess(token, user.role, user);
          navigate(getPostLoginPath(user.role), { replace: true });
        } else {
          showAppToast({ message: 'Google sign-in failed. Please try again.', type: 'error', duration: 3400 });
        }
      } catch (error) {
        const errorMsg = error.response?.data?.message || 'Google sign-in failed. Please try again.';
        showAppToast({ message: errorMsg, type: 'error', duration: 3400 });
      } finally {
        setLoading(false);
      }
    },
    onError: () => {
      showAppToast({ message: 'Google sign-in was cancelled or failed.', type: 'error', duration: 3400 });
    },
    flow: 'implicit',
    scope: 'openid email profile',
  });

  return (
    <button
      type="button"
      className="auth-page-google-btn"
      onClick={handleGoogleAuth}
      disabled={loading}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" fill="#EA4335"/>
      </svg>
      <span>Continue with Google</span>
    </button>
  );
}

// Fallback button shown when Google Client ID is not configured
function GoogleSignInDisabled({ loading }) {
  const handleClick = () => {
    showAppToast({
      message: 'Google Sign-In is not configured. Add REACT_APP_GOOGLE_CLIENT_ID to frontend/.env and check .env.example.',
      type: 'info',
      duration: 5000,
    });
  };

  return (
    <button
      type="button"
      className="auth-page-google-btn"
      onClick={handleClick}
      disabled={loading}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" fill="#EA4335"/>
      </svg>
      <span>Continue with Google</span>
    </button>
  );
}

const AuthPage = ({ onLoginSuccess }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const initialMode = searchParams.get('mode') === 'signup' ? 'signup' : 'login';
  const [mode, setMode] = useState(initialMode);
  // Shared state
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => Boolean(getRememberedLoginEmail()));
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [errors, setErrors] = useState({});

  // Login form
  const [loginEmail, setLoginEmail] = useState(() => getRememberedLoginEmail());
  const [loginPassword, setLoginPassword] = useState('');

  // Signup form
  const [signupEmail, setSignupEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Sync mode with URL
  useEffect(() => {
    const nextMode = searchParams.get('mode') === 'signup' ? 'signup' : 'login';
    setMode((currentMode) => (currentMode === nextMode ? currentMode : nextMode));
  }, [searchParams]);

  const switchMode = useCallback(
    (newMode) => {
      setMode(newMode);
      setErrors({});
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('mode', newMode);
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Password strength calculation
  const passwordStrength = useMemo(() => {
    if (!signupPassword) return { score: 0, label: '', percent: 0 };
    let score = 0;
    if (signupPassword.length >= 8) score++;
    if (signupPassword.length >= 10) score++;
    if (/[A-Z]/.test(signupPassword)) score++;
    if (/[a-z]/.test(signupPassword)) score++;
    if (/[0-9]/.test(signupPassword)) score++;
    if (/[^A-Za-z0-9]/.test(signupPassword)) score++;
    const labels = ['Too weak', 'Weak', 'Fair', 'Good', 'Strong', 'Excellent', 'Excellent'];
    const colors = ['#e44', '#e84', '#ea4', '#8c4', '#4c8', '#4c8', '#4c8'];
    return {
      score,
      label: labels[score],
      color: colors[score],
      percent: (score / 6) * 100,
    };
  }, [signupPassword]);

  // Validation helpers
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validateLogin = () => {
    const errs = {};
    if (!loginEmail) errs.loginEmail = 'Email is required';
    else if (!validateEmail(loginEmail)) errs.loginEmail = 'Please enter a valid email address';
    if (!loginPassword) errs.loginPassword = 'Password is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateSignup = () => {
    const errs = {};
    if (!firstName.trim()) errs.firstName = 'First name is required';
    if (!lastName.trim()) errs.lastName = 'Last name is required';
    if (!signupEmail) errs.signupEmail = 'Email is required';
    else if (!validateEmail(signupEmail)) errs.signupEmail = 'Please enter a valid email address';
    if (!signupPassword) errs.signupPassword = 'Password is required';
    else if (signupPassword.length < 8) errs.signupPassword = 'Password must be at least 8 characters';
    else if (!/[A-Z]/.test(signupPassword)) errs.signupPassword = 'Password must contain an uppercase letter';
    else if (!/[a-z]/.test(signupPassword)) errs.signupPassword = 'Password must contain a lowercase letter';
    else if (!/[0-9]/.test(signupPassword)) errs.signupPassword = 'Password must contain a digit';
    if (signupPassword !== confirmPassword) errs.confirmPassword = 'Passwords do not match';
    if (!agreeTerms) errs.terms = 'You must agree to the Terms & Conditions';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const persistRememberedLogin = useCallback(() => {
    try {
      if (rememberMe && loginEmail.trim()) {
        localStorage.setItem(REMEMBERED_LOGIN_EMAIL_KEY, loginEmail.trim());
      } else {
        localStorage.removeItem(REMEMBERED_LOGIN_EMAIL_KEY);
      }
    } catch (error) {
      // Remember me is convenience only; login must continue if storage is unavailable.
    }
  }, [loginEmail, rememberMe]);

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validateLogin()) return;

    setLoading(true);
    try {
      const response = await authAPI.signin({
        email: loginEmail,
        password: loginPassword,
      });

      const { token, user } = response.data;

      if (token && user) {
        persistRememberedLogin();
        setAuthToken(token, user.role);
        onLoginSuccess(token, user.role, user);
        setLoginEmail(rememberMe ? loginEmail.trim() : '');
        setLoginPassword('');
        setShowPassword(false);
        setErrors({});
        navigate(getPostLoginPath(user.role), { replace: true });
      } else {
        showAppToast({ message: 'Login failed. Please try again.', type: 'error', duration: 3400 });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Invalid email or password';
      showAppToast({ message: errorMsg, type: 'error', duration: 3400 });
    } finally {
      setLoading(false);
    }
  };

  // Handle signup
  const handleSignup = async (e) => {
    e.preventDefault();
    if (!validateSignup()) return;

    setLoading(true);
    try {
      await authAPI.signup({
        firstName,
        lastName,
        email: signupEmail,
        password: signupPassword,
      });

      showAppToast({
        message: 'Account created successfully! Please sign in.',
        type: 'success',
        duration: 2500,
      });

      // Pre-fill login email and switch to login
      setLoginEmail(signupEmail);
      switchMode('login');

      // Clear signup form
      setSignupEmail('');
      setFirstName('');
      setLastName('');
      setSignupPassword('');
      setConfirmPassword('');
      setAgreeTerms(false);
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Signup failed. Please try again.';
      showAppToast({ message: errorMsg, type: 'error', duration: 3400 });
    } finally {
      setLoading(false);
    }
  };

  const clearFieldError = (field) => {
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  return (
    <div className="auth-page">
      {/* Brand showcase panel */}
      <aside className="auth-page-showcase">
        <div className="auth-page-showcase-overlay" />
        <div className="auth-page-showcase-content">
          <div className="auth-page-showcase-text">
            <span className="auth-page-eyebrow">DeRoyalty Car Rental</span>
            <h1>Drive the Extraordinary</h1>
            <p>
              Join our exclusive circle of discerning drivers. Premium vehicles,
              seamless bookings, and a service experience worthy of royalty.
            </p>
          </div>
          <div className="auth-page-showcase-features">
            <div className="auth-page-feature-item">
              <i className="bi bi-shield-check" />
              <span>Secure &amp; Encrypted</span>
            </div>
            <div className="auth-page-feature-item">
              <i className="bi bi-car-front" />
              <span>Premium Fleet Access</span>
            </div>
            <div className="auth-page-feature-item">
              <i className="bi bi-lightning-charge" />
              <span>Instant Booking</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Form panel */}
      <main className="auth-page-form-panel">
        <div className="auth-page-form-container">
          {/* Mode toggle */}
          <div className="auth-page-toggle" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'login'}
              className={`auth-page-toggle-btn ${mode === 'login' ? 'active' : ''}`}
              onClick={() => switchMode('login')}
            >
              Sign In
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'signup'}
              className={`auth-page-toggle-btn ${mode === 'signup' ? 'active' : ''}`}
              onClick={() => switchMode('signup')}
            >
              Create Account
            </button>
            <span className="auth-page-toggle-indicator" data-active={mode} />
          </div>

          {/* Heading */}
          <div className="auth-page-heading">
            <h2>{mode === 'login' ? 'Welcome Back' : 'Begin Your Journey'}</h2>
            <p>
              {mode === 'login'
                ? 'Sign in to access your account, bookings, and wishlist.'
                : 'Create your account to start booking premium vehicles.'}
            </p>
          </div>

          {/* Google OAuth */}
          {GOOGLE_CLIENT_ID ? (
            <GoogleSignInButton
              onLoginSuccess={onLoginSuccess}
              navigate={navigate}
              setLoading={setLoading}
              loading={loading}
            />
          ) : (
            <GoogleSignInDisabled loading={loading} />
          )}

          <div className="auth-page-divider">
            <span>or</span>
          </div>

          {/* Login form */}
          {mode === 'login' && (
            <form onSubmit={handleLogin} className="auth-page-form" noValidate autoComplete="on">
              <div className="auth-page-field">
                <label htmlFor="loginEmail">Email Address</label>
                <div className="auth-page-input-wrap">
                  <i className="bi bi-envelope" />
                  <input
                    type="email"
                    id="loginEmail"
                    name="username"
                    placeholder="you@example.com"
                    value={loginEmail}
                    onChange={(e) => { setLoginEmail(e.target.value); clearFieldError('loginEmail'); }}
                    className={errors.loginEmail ? 'has-error' : ''}
                    autoComplete="username"
                  />
                </div>
                {errors.loginEmail && <span className="auth-page-error">{errors.loginEmail}</span>}
              </div>

              <div className="auth-page-field">
                <label htmlFor="loginPassword">Password</label>
                <div className="auth-page-input-wrap">
                  <i className="bi bi-lock" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="loginPassword"
                    name="password"
                    placeholder="Enter your password"
                    value={loginPassword}
                    onChange={(e) => { setLoginPassword(e.target.value); clearFieldError('loginPassword'); }}
                    className={errors.loginPassword ? 'has-error' : ''}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="auth-page-password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    tabIndex={-1}
                    aria-label="Toggle password visibility"
                  >
                    <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
                  </button>
                </div>
                {errors.loginPassword && <span className="auth-page-error">{errors.loginPassword}</span>}
              </div>

              <div className="auth-page-form-options">
                <label className="auth-page-checkbox">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span>Remember me</span>
                </label>
                <button type="button" className="auth-page-link-btn" onClick={() => showAppToast({ message: 'Password reset functionality is coming soon.', type: 'info', duration: 2500 })}>
                  Forgot password?
                </button>
              </div>

              <button type="submit" className="auth-page-submit-btn" disabled={loading}>
                {loading ? (
                  <>
                    <span className="auth-page-spinner" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <i className="bi bi-arrow-right" />
                  </>
                )}
              </button>

              <p className="auth-page-switch-text">
                Don't have an account?{' '}
                <button type="button" onClick={() => switchMode('signup')}>
                  Create one
                </button>
              </p>
            </form>
          )}

          {/* Signup form */}
          {mode === 'signup' && (
            <form onSubmit={handleSignup} className="auth-page-form" noValidate>
              <div className="auth-page-field-row">
                <div className="auth-page-field">
                  <label htmlFor="firstName">First Name</label>
                  <div className="auth-page-input-wrap">
                    <i className="bi bi-person" />
                    <input
                      type="text"
                      id="firstName"
                      placeholder="John"
                      value={firstName}
                      onChange={(e) => { setFirstName(e.target.value); clearFieldError('firstName'); }}
                      className={errors.firstName ? 'has-error' : ''}
                      autoComplete="given-name"
                    />
                  </div>
                  {errors.firstName && <span className="auth-page-error">{errors.firstName}</span>}
                </div>

                <div className="auth-page-field">
                  <label htmlFor="lastName">Last Name</label>
                  <div className="auth-page-input-wrap">
                    <i className="bi bi-person" />
                    <input
                      type="text"
                      id="lastName"
                      placeholder="Doe"
                      value={lastName}
                      onChange={(e) => { setLastName(e.target.value); clearFieldError('lastName'); }}
                      className={errors.lastName ? 'has-error' : ''}
                      autoComplete="family-name"
                    />
                  </div>
                  {errors.lastName && <span className="auth-page-error">{errors.lastName}</span>}
                </div>
              </div>

              <div className="auth-page-field">
                <label htmlFor="signupEmail">Email Address</label>
                <div className="auth-page-input-wrap">
                  <i className="bi bi-envelope" />
                  <input
                    type="email"
                    id="signupEmail"
                    placeholder="you@example.com"
                    value={signupEmail}
                    onChange={(e) => { setSignupEmail(e.target.value); clearFieldError('signupEmail'); }}
                    className={errors.signupEmail ? 'has-error' : ''}
                    autoComplete="email"
                  />
                </div>
                {errors.signupEmail && <span className="auth-page-error">{errors.signupEmail}</span>}
              </div>

              <div className="auth-page-field">
                <label htmlFor="signupPassword">Password</label>
                <div className="auth-page-input-wrap">
                  <i className="bi bi-lock" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="signupPassword"
                    placeholder="Create a password"
                    value={signupPassword}
                    onChange={(e) => { setSignupPassword(e.target.value); clearFieldError('signupPassword'); }}
                    className={errors.signupPassword ? 'has-error' : ''}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="auth-page-password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    tabIndex={-1}
                    aria-label="Toggle password visibility"
                  >
                    <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
                  </button>
                </div>
                {errors.signupPassword && <span className="auth-page-error">{errors.signupPassword}</span>}
                {signupPassword && (
                  <div className="auth-page-strength">
                    <div className="auth-page-strength-bar">
                      <div
                        className="auth-page-strength-fill"
                        style={{ width: `${passwordStrength.percent}%`, background: passwordStrength.color }}
                      />
                    </div>
                    <span className="auth-page-strength-label" style={{ color: passwordStrength.color }}>
                      {passwordStrength.label}
                    </span>
                  </div>
                )}
              </div>

              <div className="auth-page-field">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <div className="auth-page-input-wrap">
                  <i className="bi bi-lock-fill" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    placeholder="Re-enter your password"
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); clearFieldError('confirmPassword'); }}
                    className={errors.confirmPassword ? 'has-error' : ''}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="auth-page-password-toggle"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    tabIndex={-1}
                    aria-label="Toggle password visibility"
                  >
                    <i className={`bi ${showConfirmPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
                  </button>
                </div>
                {errors.confirmPassword && <span className="auth-page-error">{errors.confirmPassword}</span>}
              </div>

              <label className="auth-page-checkbox auth-page-terms">
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={(e) => { setAgreeTerms(e.target.checked); clearFieldError('terms'); }}
                />
                <span>
                  I agree to the{' '}
                  <Link to="/terms-and-conditions">Terms &amp; Conditions</Link>
                </span>
              </label>
              {errors.terms && <span className="auth-page-error">{errors.terms}</span>}

              <button type="submit" className="auth-page-submit-btn" disabled={loading}>
                {loading ? (
                  <>
                    <span className="auth-page-spinner" />
                    Creating account...
                  </>
                ) : (
                  <>
                    Create Account
                    <i className="bi bi-arrow-right" />
                  </>
                )}
              </button>

              <p className="auth-page-switch-text">
                Already have an account?{' '}
                <button type="button" onClick={() => switchMode('login')}>
                  Sign in
                </button>
              </p>
            </form>
          )}

          <p className="auth-page-back-home">
            <Link to="/home">
              <i className="bi bi-arrow-left" /> Back to home
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default AuthPage;
